import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, UserPlus, UserCheck, MapPin, Award, Star, ChefHat, Flame, Lock, Globe, Heart } from 'lucide-react';
import FollowListModal from '../components/FollowListModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ followers_count: 0, following_count: 0, is_following: false });
  const [foodlist, setFoodlist] = useState({ items: [], is_public: false });
  const [activeTab, setActiveTab] = useState('posts');
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);
  const [followModal, setFollowModal] = useState({ open: false, tab: 'followers' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, postsRes, statsRes, foodlistRes] = await Promise.all([
        fetch(`${API_URL}/api/auth/public-profile/${userId}`),
        fetch(`${API_URL}/api/posts?user_id=${userId}&limit=30`),
        fetch(`${API_URL}/api/social/stats/${userId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/foodlist/public/${userId}`),
      ]);

      if (profileRes.ok) setProfile(await profileRes.json());
      if (postsRes.ok) {
        const data = await postsRes.json();
        setPosts(data.posts || data || []);
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (foodlistRes.ok) setFoodlist(await foodlistRes.json());
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      const endpoint = stats.is_following
        ? `${API_URL}/api/social/unfollow/${userId}`
        : `${API_URL}/api/social/follow/${userId}`;
      const method = stats.is_following ? 'DELETE' : 'POST';
      const res = await fetch(endpoint, { method, credentials: 'include' });
      if (res.ok) {
        setStats(prev => ({
          ...prev,
          is_following: !prev.is_following,
          followers_count: prev.followers_count + (prev.is_following ? -1 : 1),
        }));
      }
    } catch (err) {
      console.error('Follow action failed:', err);
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500 text-lg">User not found</p>
        <button onClick={() => navigate(-1)} className="text-[#FF6B35] font-semibold">Go Back</button>
      </div>
    );
  }

  const name = profile.name || 'Foodie Explorer';
  const avatar = profile.picture || '';
  const initials = name[0]?.toUpperCase() || '?';
  const level = profile.level || 'Newbie Foodie';
  const points = profile.points || 0;
  const bio = profile.bio || '';
  const cuisines = profile.cuisine_preferences || [];
  const badges = profile.badges || [];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-profile">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-3xl mx-auto px-4 pt-4 pb-8">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-6 transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="flex items-start gap-5">
            {/* Avatar */}
            {avatar ? (
              <img src={avatar} alt={name} className="w-20 h-20 rounded-2xl object-cover ring-2 ring-white/20" />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#F4C430] flex items-center justify-center text-3xl font-bold text-white ring-2 ring-white/20">
                {initials}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold truncate" data-testid="profile-name">{name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                  <Award className="w-3 h-3 text-[#F4C430]" /> {level}
                </span>
                <span className="text-xs text-white/50">{points} pts</span>
              </div>
              {bio && <p className="text-sm text-white/60 mt-2 line-clamp-2">{bio}</p>}
            </div>
          </div>

          {/* Stats + Follow */}
          <div className="flex items-center gap-6 mt-6">
            <div className="text-center">
              <p className="text-xl font-bold" data-testid="posts-count">{profile.posts_count || posts.length}</p>
              <p className="text-[11px] text-white/50 font-medium">Posts</p>
            </div>
            <button onClick={() => setFollowModal({ open: true, tab: 'followers' })} className="text-center hover:opacity-80 transition-opacity" data-testid="open-followers-modal">
              <p className="text-xl font-bold" data-testid="followers-count">{stats.followers_count}</p>
              <p className="text-[11px] text-white/50 font-medium">Followers</p>
            </button>
            <button onClick={() => setFollowModal({ open: true, tab: 'following' })} className="text-center hover:opacity-80 transition-opacity" data-testid="open-following-modal">
              <p className="text-xl font-bold" data-testid="following-count">{stats.following_count}</p>
              <p className="text-[11px] text-white/50 font-medium">Following</p>
            </button>

            <div className="ml-auto">
              <button
                onClick={handleFollow}
                disabled={followLoading}
                data-testid="follow-btn"
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-95 ${
                  stats.is_following
                    ? 'bg-white/15 text-white border border-white/20 hover:bg-red-500/20 hover:border-red-400/40 hover:text-red-300'
                    : 'bg-[#FF6B35] text-white hover:bg-[#E5592E] shadow-lg shadow-[#FF6B35]/25'
                }`}
              >
                {followLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : stats.is_following ? (
                  <><UserCheck className="w-4 h-4" /> Following</>
                ) : (
                  <><UserPlus className="w-4 h-4" /> Follow</>
                )}
              </button>
            </div>
          </div>

          {/* Cuisine badges */}
          {cuisines.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {cuisines.slice(0, 6).map(c => (
                <span key={c} className="text-[11px] bg-white/10 text-white/70 px-2.5 py-1 rounded-full">{c}</span>
              ))}
            </div>
          )}

          {/* Achievement badges */}
          {badges.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {badges.map((b, i) => (
                <span key={i} className="text-[11px] bg-[#F4C430]/20 text-[#F4C430] px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                  <Star className="w-3 h-3" /> {b.name || b}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 flex gap-1">
          {[
            { key: 'posts', label: 'Posts', count: posts.length },
            { key: 'foodlist', label: 'Foodlist', count: foodlist.items?.length || 0, locked: !foodlist.is_public },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === tab.key
                  ? 'border-[#FF6B35] text-gray-900'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab.locked && <Lock className="w-3 h-3" />}
              {tab.label}
              {tab.count > 0 && <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded-full font-bold">{tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {activeTab === 'posts' && (
          <>
            {posts.length === 0 ? (
              <div className="text-center py-16">
                <ChefHat className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No posts yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {posts.map((post, i) => (
                  <Link
                    key={post.post_id || i}
                    to={`/post/${post.post_id}`}
                    className="group relative aspect-square rounded-2xl overflow-hidden bg-gray-100"
                    data-testid={`post-card-${i}`}
                  >
                    {post.images?.[0] ? (
                      <img src={post.images[0]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <ChefHat className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="flex items-center gap-3 text-white text-sm font-semibold">
                        <span className="flex items-center gap-1"><Heart className="w-4 h-4 fill-current" /> {post.likes_count || 0}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'foodlist' && (
          <>
            {!foodlist.is_public ? (
              <div className="text-center py-16">
                <Lock className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 text-sm font-medium">This Foodlist is private</p>
                <p className="text-gray-400 text-xs mt-1">{name} hasn't shared their Foodlist yet</p>
              </div>
            ) : foodlist.items?.length === 0 ? (
              <div className="text-center py-16">
                <Flame className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Foodlist is empty</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm text-gray-500">{foodlist.owner_name}'s public Foodlist</span>
                </div>
                {foodlist.items.map((item, i) => (
                  <div key={item.item_id || i} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 hover:shadow-sm transition-shadow" data-testid={`foodlist-item-${i}`}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-xl object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center">
                        <ChefHat className="w-6 h-6 text-gray-300" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{item.name || item.dish_name || 'Saved dish'}</p>
                      {item.venue_name && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" /> {item.venue_name}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <FollowListModal
        isOpen={followModal.open}
        onClose={() => setFollowModal({ open: false, tab: 'followers' })}
        tab={followModal.tab}
        userId={userId}
      />
    </div>
  );
};

export default PublicProfile;
